/**
 * フォーム共通バリデーション関数
 * メールアドレス、パスワード、電話番号など、よく使われるバリデーション処理をまとめたユーティリティ
 */
import { PREFECTURES } from '../data/prefectures';

// ■ バリデーション関数群
export const validators = {
  /**
   * 必須項目のチェック（空文字・null・undefined・空白のみを除外）
   * @param {any} value - 検証する値
   * @returns {boolean} 有効な値かどうか
   */
  required: (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
  },

  /**
   * メールアドレスの形式をチェック
   * @param {string} email - 検証するメールアドレス
   * @returns {boolean} 有効な形式かどうか
   */
  email: (email) => {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  },

  /**
   * パスワードの基本要件をチェック
   * @param {string} password - 検証するパスワード
   * @param {Object} options - バリデーションオプション
   * @param {number} options.minLength - 最小文字数（デフォルト: 8）
   * @param {boolean} options.requireUppercase - 大文字必須（デフォルト: false）
   * @param {boolean} options.requireLowercase - 小文字必須（デフォルト: false）
   * @param {boolean} options.requireNumbers - 数字必須（デフォルト: false）
   * @returns {boolean} 有効なパスワードかどうか
   */
  password: (password, options = {}) => {
    if (!password || typeof password !== 'string') return false;

    const {
      minLength = 8,
      requireUppercase = false,
      requireLowercase = false,
      requireNumbers = false
    } = options;

    // 最小文字数チェック
    if (password.length < minLength) return false;

    // 大文字必須チェック
    if (requireUppercase && !/[A-Z]/.test(password)) return false;

    // 小文字必須チェック
    if (requireLowercase && !/[a-z]/.test(password)) return false;

    // 数字必須チェック
    if (requireNumbers && !/[0-9]/.test(password)) return false;

    return true;
  },

  /**
   * パスワード強度の詳細チェック（段階的評価用）
   * @param {string} password - 検証するパスワード
   * @returns {Object} 各要件の満足状況
   */
  passwordStrength: (password) => {
    if (!password || typeof password !== 'string') {
      return {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false
      };
    }

    return {
      length: password.length >= 8,           // 8文字以上
      uppercase: /[A-Z]/.test(password),      // 英大文字（A-Z）を含む
      lowercase: /[a-z]/.test(password),      // 英小文字（a-z）を含む
      number: /[0-9]/.test(password)          // 数字（0-9）を含む
    };
  },

  /**
   * パスワード強度の総合判定
   * @param {Object} strength - passwordStrengthの結果
   * @returns {boolean} 全ての要件を満たしているかどうか
   */
  isPasswordValid: (strength) => {
    return Object.values(strength).every(Boolean);
  },

  /**
   * パスワード確認の一致チェック
   * @param {string} password - 元のパスワード
   * @param {string} confirmPassword - 確認用パスワード
   * @returns {boolean} 一致するかどうか
   */
  passwordMatch: (password, confirmPassword) => {
    return password === confirmPassword;
  },

  /**
   * 日本の電話番号形式をチェック（固定電話・携帯電話）
   * @param {string} phoneNumber - 検証する電話番号
   * @returns {boolean} 有効な形式かどうか
   */
  phoneNumber: (phoneNumber) => {
    if (!phoneNumber || typeof phoneNumber !== 'string') return false;
    
    // ハイフンあり・なし両方に対応
    // 固定電話: 0XX-XXXX-XXXX、携帯電話: 070/080/090-XXXX-XXXX
    const phoneRegex = /^(0\d{1,4}-?\d{1,4}-?\d{4}|070-?\d{4}-?\d{4}|080-?\d{4}-?\d{4}|090-?\d{4}-?\d{4})$/;
    return phoneRegex.test(phoneNumber.trim());
  },

  /**
   * 郵便番号の形式をチェック（XXX-XXXX形式）
   * @param {string} postalCode - 検証する郵便番号
   * @returns {boolean} 有効な形式かどうか
   */
  postalCode: (postalCode) => {
    if (!postalCode || typeof postalCode !== 'string') return false;
    
    // ハイフンあり形式（XXX-XXXX）のみ許可
    return /^\d{3}-\d{4}$/.test(postalCode.trim());
  },

  /**
   * ひらがなのみの入力をチェック
   * @param {string} value - 検証する値
   * @returns {boolean} ひらがなのみかどうか
   */
  hiragana: (value) => {
    if (!value || typeof value !== 'string') return false;
    
    // ひらがな、スペース、長音符のみを許可
    const hiraganaRegex = /^[あ-んー\sー]*$/;
    return hiraganaRegex.test(value.trim());
  },

  /**
   * 認証コードの形式をチェック（6桁の数字）
   * @param {string} code - 検証する認証コード
   * @returns {boolean} 有効な形式かどうか
   */
  verificationCode: (code) => {
    if (!code || typeof code !== 'string') return false;
    return code.length === 6 && /^\d{6}$/.test(code);
  },

  /**
   * 都道府県の有効性をチェック
   * @param {string} prefecture - 検証する都道府県名
   * @returns {boolean} 有効な都道府県名かどうか
   */
  prefecture: (prefecture) => {
    if (!prefecture || typeof prefecture !== 'string') return false;
    
    // PREFECTURESのlabelに含まれているかチェック（空文字の選択肢は除外）
    const validPrefectures = PREFECTURES
      .filter(p => p.value !== '')
      .map(p => p.label);
    
    return validPrefectures.includes(prefecture.trim());
  }
};

// ■ エラーメッセージ定数
export const VALIDATION_MESSAGES = {
  // 基本的な必須項目
  REQUIRED: '入力が必要です',
  
  // メールアドレス関連
  EMAIL_REQUIRED: 'メールアドレスを入力してください',
  EMAIL_INVALID: 'メールアドレスの形式が正しくありません',
  
  // パスワード関連
  PASSWORD_REQUIRED: 'パスワードを入力してください',
  PASSWORD_TOO_SHORT: (minLength) => `パスワードは${minLength}文字以上で入力してください`,
  PASSWORD_NEEDS_UPPERCASE: 'パスワードに英大文字を含めてください',
  PASSWORD_NEEDS_LOWERCASE: 'パスワードに英小文字を含めてください',
  PASSWORD_NEEDS_NUMBERS: 'パスワードに数字を含めてください',
  PASSWORD_MISMATCH: 'パスワードが一致しません',
  
  // 電話番号関連
  PHONE_REQUIRED: '電話番号を入力してください',
  PHONE_INVALID: '電話番号の形式が正しくありません',

  // 郵便番号関連
  POSTAL_CODE_REQUIRED: '郵便番号を入力してください',
  POSTAL_CODE_INVALID: '郵便番号はXXX-XXXX形式で入力してください',
  
  // ひらがな関連
  HIRAGANA_REQUIRED: 'ひらがなで入力してください',
  HIRAGANA_INVALID: 'ひらがなで入力してください（漢字、カタカナ、英数字は使用できません）',
  
  // 認証コード関連
  VERIFICATION_CODE_REQUIRED: '認証コードを入力してください',
  VERIFICATION_CODE_INVALID: '認証コードは6桁の数字で入力してください',

  // 都道府県関連
  PREFECTURE_REQUIRED: '都道府県を入力してください',
  PREFECTURE_INVALID: '有効な都道府県名を入力してください',

  // ご利用開始日関連
  SERVICE_START_DATE_TOO_FAR: 'ご入会希望日は1カ月以内に設定をお願いします'
};

// ■ バリデーション実行関数
/**
 * メールアドレスのバリデーション
 * @param {string} email - 検証するメールアドレス
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validateEmail = (email) => {
  if (!validators.required(email)) {
    return VALIDATION_MESSAGES.EMAIL_REQUIRED;
  }
  if (!validators.email(email)) {
    return VALIDATION_MESSAGES.EMAIL_INVALID;
  }
  return '';
};

/**
 * パスワードのバリデーション
 * @param {string} password - 検証するパスワード
 * @param {Object} options - バリデーションオプション
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePassword = (password, options = {}) => {
  const { minLength = 8, requireStrong = true } = options;
  
  if (!validators.required(password)) {
    return VALIDATION_MESSAGES.PASSWORD_REQUIRED;
  }
  
  if (password.length < minLength) {
    return VALIDATION_MESSAGES.PASSWORD_TOO_SHORT(minLength);
  }
  
  // 強力なパスワードが必要な場合の追加チェック
  if (!requireStrong) {
    return '';
  }

  // パスワード強度をチェック
  const strength = validators.passwordStrength(password);

  if (!strength.uppercase) {
    return VALIDATION_MESSAGES.PASSWORD_NEEDS_UPPERCASE;
  }
  if (!strength.lowercase) {
    return VALIDATION_MESSAGES.PASSWORD_NEEDS_LOWERCASE;
  }
  if (!strength.number) {
    return VALIDATION_MESSAGES.PASSWORD_NEEDS_NUMBERS;
  }
  return '';
};

/**
 * パスワード確認のバリデーション
 * @param {string} password - 元のパスワード
 * @param {string} confirmPassword - 確認用パスワード
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePasswordConfirm = (password, confirmPassword) => {
  if (!validators.required(confirmPassword)) {
    return VALIDATION_MESSAGES.PASSWORD_REQUIRED;
  }
  if (!validators.passwordMatch(password, confirmPassword)) {
    return VALIDATION_MESSAGES.PASSWORD_MISMATCH;
  }
  return '';
};

/**
 * 電話番号のバリデーション
 * @param {string} phoneNumber - 検証する電話番号
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!validators.required(phoneNumber)) {
    return VALIDATION_MESSAGES.PHONE_REQUIRED;
  }
  if (!validators.phoneNumber(phoneNumber)) {
    return VALIDATION_MESSAGES.PHONE_INVALID;
  }
  return '';
};

/**
 * 郵便番号のバリデーション
 * @param {string} postalCode - 検証する郵便番号
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePostalCode = (postalCode) => {
  if (!validators.required(postalCode)) {
    return VALIDATION_MESSAGES.POSTAL_CODE_REQUIRED;
  }
  if (!validators.postalCode(postalCode)) {
    return VALIDATION_MESSAGES.POSTAL_CODE_INVALID;
  }
  return '';
};

/**
 * 郵便番号を自動フォーマットする関数
 * @param {string} value - 入力された値
 * @returns {string} フォーマットされた郵便番号（XXX-XXXX形式）
 */
export const formatPostalCode = (value) => {
  // 数字のみを抽出（ハイフンも一旦削除）
  const numbers = value.replace(/[^\d]/g, '');
  
  // 7桁以上の場合は7桁に制限
  const limited = numbers.slice(0, 7);
  
  // 3桁以下の場合はそのまま返す
  if (limited.length <= 3) {
    return limited;
  }
  
  // 4桁以上の場合は「XXX-XXXX」形式にフォーマット
  return `${limited.slice(0, 3)}-${limited.slice(3)}`;
};

/**
 * ひらがなのバリデーション
 * @param {string} value - 検証する値
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validateHiragana = (value) => {
  if (!validators.required(value)) {
    return VALIDATION_MESSAGES.HIRAGANA_REQUIRED;
  }
  if (!validators.hiragana(value)) {
    return VALIDATION_MESSAGES.HIRAGANA_INVALID;
  }
  return '';
};

/**
 * 認証コードのバリデーション
 * @param {string} code - 検証する認証コード
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validateVerificationCode = (code) => {
  if (!validators.required(code)) {
    return VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED;
  }
  if (!validators.verificationCode(code)) {
    return VALIDATION_MESSAGES.VERIFICATION_CODE_INVALID;
  }
  return '';
};

/**
 * 都道府県のバリデーション
 * @param {string} prefecture - 検証する都道府県名
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePrefecture = (prefecture) => {
  if (!validators.required(prefecture)) {
    return VALIDATION_MESSAGES.PREFECTURE_REQUIRED;
  }
  if (!validators.prefecture(prefecture)) {
    return VALIDATION_MESSAGES.PREFECTURE_INVALID;
  }
  return '';
};

// ■ 便利な複合バリデーション関数
/**
 * フォームフィールドの汎用バリデーション
 * @param {string} fieldType - フィールドの種類
 * @param {any} value - 検証する値
 * @param {Object} options - オプション
 * @returns {string} エラーメッセージ（エラーがない場合は空文字）
 */
export const validateField = (fieldType, value, options = {}) => {
  switch (fieldType) {
    case 'email':
      return validateEmail(value);
    case 'password':
      return validatePassword(value, options);
    case 'passwordConfirm':
      return validatePasswordConfirm(options.password, value);
    case 'phoneNumber':
      return validatePhoneNumber(value);
    case 'postalCode':
      return validatePostalCode(value);
    case 'hiragana':
      return validateHiragana(value);
    case 'verificationCode':
      return validateVerificationCode(value);
    case 'prefecture':
      return validatePrefecture(value);
    case 'required':
      return validators.required(value) ? '' : VALIDATION_MESSAGES.REQUIRED;
    default:
      return '';
  }
};

/**
 * パスワード強度をチェックして詳細な状況を返す
 * @param {string} password - 検証するパスワード
 * @returns {Object} 各要件の満足状況と総合判定
 */
export const checkPasswordStrength = (password) => {
  const strength = validators.passwordStrength(password);
  const isValid = validators.isPasswordValid(strength);
  
  return {
    ...strength,
    isValid,
    score: Object.values(strength).filter(Boolean).length
  };
};

/**
 * パスワード強度判定関数
 * @param {Object} strength - passwordStrengthの結果
 * @returns {boolean} 全ての要件を満たしているかどうか
 */
export const isPasswordValid = (strength) => {
  return validators.isPasswordValid(strength);
};

// デフォルトエクスポート
const formValidation = {
  validators,
  VALIDATION_MESSAGES,
  validateEmail,
  validatePassword,
  validatePasswordConfirm,
  validatePhoneNumber,
  validatePostalCode,
  validateHiragana,
  validateVerificationCode,
  validatePrefecture,
  validateField,
  checkPasswordStrength,
  isPasswordValid,
  formatPostalCode
};

export default formValidation;